import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { register } from "../services/api";
import TenantLogo from "../components/shared/TenantLogo";
import { useTenant } from "../context/TenantContext";

const isHex = (c) => typeof c === "string" && /^#([0-9A-F]{3}){1,2}$/i.test(c);
const safeColor = (c, fallback) => (isHex(c) ? c : fallback);

const Section = ({ title, primaryColor, children }) => (
  <div className="rounded-2xl border border-slate-700 bg-slate-800/25 p-4 sm:p-5">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-semibold text-white">{title}</h3>
      <span
        className="inline-block h-1.5 w-10 rounded-full"
        style={{ background: `linear-gradient(90deg, ${primaryColor}, transparent)` }}
        aria-hidden="true"
      />
    </div>
    {children}
  </div>
);

export default function Register({ onSuccess, onBackToLogin }) {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    password_confirm: "",
    first_name: "",
    last_name: "",
    dni: "",
    telefono: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const tenantConfig = useTenant();

  const { primaryColor, secondaryColor } = useMemo(() => {
    const primary = safeColor(tenantConfig?.color_primario, "#6366F1");
    const secondary = safeColor(tenantConfig?.color_secundario, "#4F46E5");
    return { primaryColor: primary, secondaryColor: secondary };
  }, [tenantConfig]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) newErrors.username = "El usuario es requerido";
    else if (formData.username.trim().length < 3) newErrors.username = "Mínimo 3 caracteres";

    if (!formData.email.trim()) newErrors.email = "El email es requerido";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim()))
      newErrors.email = "Email inválido";

    if (!formData.password) newErrors.password = "La contraseña es requerida";
    else if (formData.password.length < 6) newErrors.password = "Mínimo 6 caracteres";

    if (!formData.password_confirm) newErrors.password_confirm = "Confirmá la contraseña";
    else if (formData.password !== formData.password_confirm)
      newErrors.password_confirm = "Las contraseñas no coinciden";

    if (!formData.first_name.trim()) newErrors.first_name = "El nombre es requerido";
    if (!formData.last_name.trim()) newErrors.last_name = "El apellido es requerido";

    if (!formData.dni.trim()) {
      newErrors.dni = "El DNI es requerido";
    } else if (!/^\d{6,10}$/.test(formData.dni.trim())) {
      newErrors.dni = "DNI inválido (solo números, 6 a 10 dígitos)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const fieldClass = (hasError) =>
    `w-full rounded-xl border bg-slate-800/40 px-4 py-3 text-white placeholder-slate-500 transition
     focus:outline-none focus:ring-2 focus:border-transparent disabled:opacity-60
     ${hasError ? "border-red-500" : "border-slate-700"}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!validateForm()) {
      toast.error("Revisá los campos marcados", { icon: "⚠️", duration: 4000 });
      return;
    }

    setLoading(true);

    const loadingToast = toast.loading("Creando tu cuenta...", {
      style: { borderLeft: `4px solid ${primaryColor}` },
    });

    try {
      const data = await register({
          username: formData.username.trim(),
          email: formData.email.trim(),
          password: formData.password,
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          dni: formData.dni.trim(),
          telefono: formData.telefono?.trim(),
        });

      if (data?.error) {
        toast.error(data.error, { duration: 5000, icon: "❌" });
        return;
      }

      toast.success("Cuenta creada. Ahora podés iniciar sesión.", {
        icon: "✅",
        duration: 3500,
      });

      onSuccess?.();
    } catch (err) {
      toast.error("Error al crear la cuenta. Intentá nuevamente.", {
        duration: 5000,
        icon: "❌",
      });
    } finally {
      toast.dismiss(loadingToast);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div
        className="relative w-full max-w-2xl overflow-hidden rounded-2xl border bg-slate-900/55 backdrop-blur-xl
                   shadow-[0_25px_80px_-45px_rgba(0,0,0,0.9)]"
        style={{ borderColor: `${primaryColor}22` }}
      >
        {/* top accent */}
        <div
          className="absolute inset-x-0 top-0 h-[2px]"
          style={{ background: `linear-gradient(90deg, ${primaryColor}, transparent)` }}
        />

        <div className="p-7 sm:p-8">
          {/* Header */}
          <div className="text-center mb-7">
            <div
              className="mx-auto grid place-items-center rounded-2xl border"
              style={{
                width: 56,
                height: 56,
                borderColor: `${primaryColor}35`,
                backgroundColor: `${primaryColor}12`,
              }}
              aria-label="Logo"
            >
              <TenantLogo tenantConfig={tenantConfig} size="large" className="block" />
            </div>

            <h2 className="mt-4 text-2xl sm:text-3xl font-semibold tracking-tight text-white">
              Crear cuenta
            </h2>

            <p className="mt-2 text-sm font-medium" style={{ color: primaryColor }}>
              {tenantConfig?.nombre || "Gimnasio"}
            </p>

            <p className="mt-1 text-sm text-slate-400">
              {tenantConfig?.mensaje_registro || "Registrate para comenzar a entrenar"}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <Section title="Datos de cuenta" primaryColor={primaryColor}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* username */}
                <div>
                  <label className="block text-xs font-medium tracking-wide text-slate-300 mb-2">
                    Usuario <span className="text-red-400">*</span>
                  </label>
                  <input
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="username"
                    placeholder="usuario123"
                    className={fieldClass(!!errors.username)}
                    style={{ "--tw-ring-color": primaryColor }}
                  />
                  {errors.username && (
                    <p className="text-red-400 text-xs mt-1">{errors.username}</p>
                  )}
                </div>

                {/* email */}
                <div>
                  <label className="block text-xs font-medium tracking-wide text-slate-300 mb-2">
                    Email <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="email"
                    placeholder="tu@email.com"
                    className={fieldClass(!!errors.email)}
                    style={{ "--tw-ring-color": primaryColor }}
                  />
                  {errors.email && (
                    <p className="text-red-400 text-xs mt-1">{errors.email}</p>
                  )}
                </div>

                {/* password */}
                <div>
                  <label className="block text-xs font-medium tracking-wide text-slate-300 mb-2">
                    Contraseña <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="new-password"
                    placeholder="Mínimo 6 caracteres"
                    className={fieldClass(!!errors.password)}
                    style={{ "--tw-ring-color": primaryColor }}
                  />
                  {errors.password && (
                    <p className="text-red-400 text-xs mt-1">{errors.password}</p>
                  )}
                </div>

                {/* confirm */}
                <div>
                  <label className="block text-xs font-medium tracking-wide text-slate-300 mb-2">
                    Confirmar contraseña <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="password"
                    name="password_confirm"
                    value={formData.password_confirm}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="new-password"
                    placeholder="Repetí tu contraseña"
                    className={fieldClass(!!errors.password_confirm)}
                    style={{ "--tw-ring-color": primaryColor }}
                  />
                  {errors.password_confirm && (
                    <p className="text-red-400 text-xs mt-1">{errors.password_confirm}</p>
                  )}
                </div>
              </div>
            </Section>

            <Section title="Datos personales" primaryColor={primaryColor}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* first name */}
                <div>
                  <label className="block text-xs font-medium tracking-wide text-slate-300 mb-2">
                    Nombre <span className="text-red-400">*</span>
                  </label>
                  <input
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="given-name"
                    placeholder="Juan"
                    className={fieldClass(!!errors.first_name)}
                    style={{ "--tw-ring-color": primaryColor }}
                  />
                  {errors.first_name && (
                    <p className="text-red-400 text-xs mt-1">{errors.first_name}</p>
                  )}
                </div>

                {/* last name */}
                <div>
                  <label className="block text-xs font-medium tracking-wide text-slate-300 mb-2">
                    Apellido <span className="text-red-400">*</span>
                  </label>
                  <input
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="family-name"
                    placeholder="Pérez"
                    className={fieldClass(!!errors.last_name)}
                    style={{ "--tw-ring-color": primaryColor }}
                  />
                  {errors.last_name && (
                    <p className="text-red-400 text-xs mt-1">{errors.last_name}</p>
                  )}
                </div>
              </div>

              {/* DNI + phone */}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium tracking-wide text-slate-300 mb-2">
                    DNI <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="dni"
                    value={formData.dni}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="12345678"
                    maxLength={10}
                    className={fieldClass(!!errors.dni)}
                    style={{ "--tw-ring-color": primaryColor }}
                  />
                  {errors.dni && (
                    <p className="text-red-400 text-xs mt-1">{errors.dni}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium tracking-wide text-slate-300 mb-2">
                    Teléfono <span className="text-slate-500">(opcional)</span>
                  </label>
                  <input
                    type="tel"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="tel"
                    placeholder="+549..."
                    className={fieldClass(false)}
                    style={{ "--tw-ring-color": primaryColor }}
                  />
                </div>
              </div>
            </Section>

            {/* Actions */}
            <div className="pt-2 flex flex-col gap-3">
              <button
                type="submit"
                disabled={loading}
                aria-busy={loading}
                className="w-full rounded-xl px-4 py-3 text-sm font-semibold text-white transition
                           hover:scale-[1.01] active:scale-[0.99]
                           focus:outline-none focus:ring-2 focus:ring-offset-0
                           disabled:hover:scale-100 disabled:cursor-not-allowed disabled:opacity-60"
                style={{
                  background: `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`,
                  boxShadow: `0 18px 40px -22px ${primaryColor}80`,
                  "--tw-ring-color": primaryColor,
                }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-3">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Creando cuenta...
                  </span>
                ) : (
                  "Crear cuenta"
                )}
              </button>

              <button
                type="button"
                onClick={onBackToLogin}
                disabled={loading}
                className="w-full rounded-xl border border-slate-700 bg-slate-800/35 px-4 py-3 text-sm font-semibold text-white transition
                           hover:bg-slate-800/55 focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:opacity-60"
                style={{ "--tw-ring-color": primaryColor }}
              >
                Volver al login
              </button>
            </div>
          </form>

          <p className="mt-6 text-center text-xs text-slate-500">
            Al crear una cuenta aceptás las políticas del gimnasio.
          </p>
        </div>
      </div>
    </div>
  );
}
